import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Save, X, ImagePlus, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const TABS = ["Weight Loss (-10kg+)", "Inch Loss & Toning", "Busy Professional"];
const CATEGORY_TAGS = [
  "Working Professional",
  "Desk-Worker Protocol",
  "PCOD / Lifestyle Recomp",
  "Lifestyle Recomp",
];

const EMPTY = {
  name: "",
  result: "",
  category_tag: CATEGORY_TAGS[0],
  tab: TABS[0],
  before: "",
  after: "",
  duration: "",
  quote: "",
  image_url: "",
  published: true,
};

const inputCls =
  "w-full rounded-xl bg-ink border border-line px-4 py-3 text-white placeholder:text-slate-600 focus:border-accent focus:outline-none transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2";

const mediaSrc = (url) =>
  !url ? "" : url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

export default function TransformationsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      setItems(await api("/transformations/cards/all"));
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(formatApiError(data?.detail));
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded — now save the card.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.result.trim()) {
      toast.error("Client name and metric badge are required.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api(`/transformations/cards/${editingId}`, { method: "PUT", body: JSON.stringify(form) });
        toast.success("Transformation updated — live on the homepage.");
      } else {
        await api("/transformations/cards", { method: "POST", body: JSON.stringify(form) });
        toast.success("Transformation published to the homepage grid.");
      }
      reset();
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      result: item.result,
      category_tag: item.category_tag || CATEGORY_TAGS[0],
      tab: item.tab || TABS[0],
      before: item.before || "",
      after: item.after || "",
      duration: item.duration || "",
      quote: item.quote || "",
      image_url: item.image_url || "",
      published: item.published,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const togglePublished = async (item) => {
    setBusyId(item.id);
    try {
      await api(`/transformations/cards/${item.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: item.name,
          result: item.result,
          category_tag: item.category_tag,
          tab: item.tab,
          before: item.before,
          after: item.after,
          duration: item.duration,
          quote: item.quote,
          image_url: item.image_url,
          published: !item.published,
        }),
      });
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete the transformation card for ${item.name}?`)) return;
    setBusyId(item.id);
    try {
      await api(`/transformations/cards/${item.id}`, { method: "DELETE" });
      toast.success("Transformation removed from the homepage.");
      if (editingId === item.id) reset();
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="transformations-manager">
      <form
        onSubmit={save}
        className="rounded-3xl border border-line bg-panel p-7 lg:p-9"
        data-testid="transformation-form"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-extrabold uppercase text-white">
              {editingId ? "Edit Transformation" : "Manage Transformations"}
            </h2>
            <p className="mt-1.5 text-sm text-slate-400">
              Saved cards appear instantly in the homepage transformation grid.
            </p>
          </div>
          {editingId && (
            <button
              type="button"
              onClick={reset}
              data-testid="transformation-cancel-edit-btn"
              className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-300 transition-colors hover:border-slate-500 hover:text-white"
            >
              <X className="h-3.5 w-3.5" /> Cancel edit
            </button>
          )}
        </div>

        <div className="mt-7 grid sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Client Name / Initials</label>
            <input
              value={form.name}
              onChange={set("name")}
              placeholder="Vaibhav K."
              className={inputCls}
              data-testid="transformation-name-input"
            />
          </div>
          <div>
            <label className={labelCls}>Metric Badge</label>
            <input
              value={form.result}
              onChange={set("result")}
              placeholder="-12 kg Fat Loss"
              className={inputCls}
              data-testid="transformation-result-input"
            />
          </div>
          <div>
            <label className={labelCls}>Category Tag</label>
            <select
              value={form.category_tag}
              onChange={set("category_tag")}
              className={inputCls}
              data-testid="transformation-category-select"
            >
              {CATEGORY_TAGS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Homepage Filter Tab</label>
            <select
              value={form.tab}
              onChange={set("tab")}
              className={inputCls}
              data-testid="transformation-tab-select"
            >
              {TABS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:col-span-2">
            <div>
              <label className={labelCls}>Before</label>
              <input
                value={form.before}
                onChange={set("before")}
                placeholder="92 kg"
                className={inputCls}
                data-testid="transformation-before-input"
              />
            </div>
            <div>
              <label className={labelCls}>After</label>
              <input
                value={form.after}
                onChange={set("after")}
                placeholder="80 kg"
                className={inputCls}
                data-testid="transformation-after-input"
              />
            </div>
            <div>
              <label className={labelCls}>Duration</label>
              <input
                value={form.duration}
                onChange={set("duration")}
                placeholder="12 Weeks"
                className={inputCls}
                data-testid="transformation-duration-input"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Before / After Image</label>
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => uploadImage(e.target.files?.[0])}
                className="hidden"
                data-testid="transformation-file-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="transformation-upload-btn"
                className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-300 transition-colors hover:border-accent/50 hover:text-emerald-300 disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                {uploading ? "Uploading..." : "Upload Image"}
              </button>
              <input
                value={form.image_url}
                onChange={set("image_url")}
                placeholder="…or paste an image URL"
                className={`${inputCls} flex-1 min-w-[220px]`}
                data-testid="transformation-image-url-input"
              />
              {form.image_url && (
                <img
                  src={mediaSrc(form.image_url)}
                  alt="Transformation preview"
                  className="h-16 w-14 rounded-lg border border-line object-cover"
                  data-testid="transformation-image-preview"
                />
              )}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Short Testimonial Quote</label>
            <textarea
              value={form.quote}
              onChange={set("quote")}
              rows={3}
              placeholder="Down 12 kg in 12 weeks without giving up home-cooked Indian food."
              className={`${inputCls} resize-y`}
              data-testid="transformation-quote-input"
            />
          </div>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-4">
          <button
            type="submit"
            disabled={saving}
            data-testid="transformation-save-btn"
            className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-accent px-7 py-3.5 text-sm font-extrabold uppercase tracking-wide text-ink transition-all duration-300 hover:-translate-y-0.5 hover:shadow-glow-emerald disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {editingId ? "Save Changes" : "Save & Publish"}
          </button>
          <label className="flex cursor-pointer items-center gap-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
              className="h-4 w-4 accent-emerald-500"
              data-testid="transformation-published-checkbox"
            />
            Visible on homepage
          </label>
        </div>
      </form>

      <div className="rounded-3xl border border-line bg-panel overflow-hidden">
        <div className="flex items-center gap-3 border-b border-line px-7 py-5">
          <Plus className="h-4 w-4 text-emerald-400" />
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
            Published cards · {items.filter((i) => i.published).length} of {items.length}
          </p>
        </div>
        <ul className="divide-y divide-line" data-testid="transformation-list">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex flex-wrap items-center gap-4 px-7 py-5"
              data-testid={`transformation-row-${item.id}`}
            >
              <div className="h-16 w-14 shrink-0 overflow-hidden rounded-lg border border-line bg-ink">
                {item.image_url ? (
                  <img src={mediaSrc(item.image_url)} alt={item.name} className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full w-full items-center justify-center font-mono text-[9px] uppercase text-slate-600">
                    No img
                  </span>
                )}
              </div>
              <div className="min-w-[180px] flex-1">
                <p className="font-display text-lg font-bold uppercase text-white">{item.name}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-emerald-300">{item.result}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {item.tab} · {item.category_tag}
                  {item.duration ? ` · ${item.duration}` : ""}
                </p>
              </div>
              <p className="hidden lg:block max-w-xs flex-1 text-xs italic text-slate-400">"{item.quote}"</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => togglePublished(item)}
                  disabled={busyId === item.id}
                  title={item.published ? "Hide from homepage" : "Show on homepage"}
                  data-testid={`transformation-toggle-btn-${item.id}`}
                  className={`rounded-full border p-2.5 transition-colors ${
                    item.published
                      ? "border-accent/40 text-emerald-300 hover:border-accent"
                      : "border-line text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {item.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => edit(item)}
                  title="Edit"
                  data-testid={`transformation-edit-btn-${item.id}`}
                  className="rounded-full border border-line p-2.5 text-slate-400 transition-colors hover:border-gold/50 hover:text-gold"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(item)}
                  disabled={busyId === item.id}
                  title="Delete"
                  data-testid={`transformation-delete-btn-${item.id}`}
                  className="rounded-full border border-line p-2.5 text-slate-400 transition-colors hover:border-rose-400/50 hover:text-rose-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
          {!items.length && (
            <li className="px-7 py-14 text-center font-mono text-sm text-slate-500" data-testid="transformation-empty">
              No transformation cards yet — add your first one above.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
